// Client API admin scheda paziente (Sprint 4 — espansione di Sprint 3).

import { z } from 'zod';
import { request } from './_request';

const IdentitySchema = z.object({
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  date_of_birth: z.string().nullable(),
  fiscal_code: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
});

const IdentityMetaSchema = z.object({
  collected_at: z.string(),
  collection_method: z.string(),
  last_decrypted_at: z.string().nullable(),
});

const PatientDetailSchema = z.object({
  ok: z.literal(true),
  patient: z.object({
    id: z.string().uuid(),
    pseudonym: z.string(),
    notes: z.string().nullable(),
    operator_notes: z.string().nullable(),
    notes_updated_at: z.string().nullable(),
    notes_updated_by_admin_id: z.string().nullable(),
    created_at: z.string(),
    last_seen_at: z.string(),
  }),
  identity: IdentitySchema.nullable(),
  identity_meta: IdentityMetaSchema.nullable(),
  encounters: z.array(
    z.object({
      id: z.string().uuid(),
      status: z.string(),
      languageDetected: z.string().nullable(),
      languageScript: z.string().nullable(),
      totalTurns: z.number(),
      completenessPct: z.number(),
      startedAt: z.string(),
      completedAt: z.string().nullable(),
      durationSeconds: z.number().nullable(),
      emergencyTriggered: z.boolean(),
      reviewedAt: z.string().nullable(),
      markedClinicalRelevant: z.boolean(),
      pickupCode: z.string(),
      flag_counts: z.object({
        critical: z.number(),
        warning: z.number(),
        info: z.number(),
      }),
      outcomes_count: z.number(),
    }),
  ),
});

export type PatientDetail = z.infer<typeof PatientDetailSchema>;

const PatientListSchema = z.object({
  ok: z.literal(true),
  page: z.number(),
  limit: z.number(),
  total: z.number(),
  patients: z.array(
    z.object({
      id: z.string().uuid(),
      pseudonym: z.string(),
      created_at: z.string(),
      last_seen_at: z.string(),
      operator_notes: z.string().nullable(),
      has_identity: z.boolean(),
      first_name: z.string().nullable().optional(),
      last_name: z.string().nullable().optional(),
      encounters_count: z.number(),
      last_encounter_at: z.string().nullable(),
      red_flags_count: z.number(),
      languages: z.array(z.string()),
    }),
  ),
});

export type PatientList = z.infer<typeof PatientListSchema>;

export interface PatientListQuery {
  search?: string;
  has_red_flags?: 'true' | 'false';
  language?: string;
  page?: number;
  limit?: number;
}

export async function listPatients(q: PatientListQuery = {}): Promise<PatientList> {
  const params = new URLSearchParams();
  if (q.search) params.set('search', q.search);
  if (q.has_red_flags) params.set('has_red_flags', q.has_red_flags);
  if (q.language) params.set('language', q.language);
  if (q.page) params.set('page', String(q.page));
  if (q.limit) params.set('limit', String(q.limit));
  const qs = params.toString();
  const json = await request<unknown>(`/api/admin/patients${qs ? '?' + qs : ''}`);
  return PatientListSchema.parse(json);
}

export async function getPatientByPseudonym(pseudonym: string): Promise<PatientDetail> {
  const json = await request<unknown>(`/api/admin/patients/${encodeURIComponent(pseudonym)}`);
  return PatientDetailSchema.parse(json);
}

export async function updatePatientNotes(pseudonym: string, notes: string): Promise<{ ok: true }> {
  const json = await request<unknown>(
    `/api/admin/patients/${encodeURIComponent(pseudonym)}/notes`,
    {
      method: 'PATCH',
      body: JSON.stringify({ notes }),
    },
  );
  return z.object({ ok: z.literal(true) }).parse(json);
}
